// SpecialCardModule.tsx
import React, { useState } from 'react';
import { StructuredSpecialCard } from './StructuredSpecialCard';
import { IntroBulletsSpecialCard } from './IntroBulletsSpecialCard';

export interface SpecialData {
  logoUrl?: string;
  imageUrl?: string;
  name: string;
  description: string;
  tone?: string;
  format?: string;
}

interface SpecialCardProps {
  input: string | SpecialData;
}

const parseSpecialData = (input: string | SpecialData): SpecialData => {
  if (typeof input !== 'string') {
    // If it's already an object, check for format
    if (!input.format && input.description) {
      // Detect format if not already set
      if (input.description.includes('⸻') || 
          input.description.includes('---') ||
          input.description.match(/^\d+\.\s+/m) ||
          input.description.includes('Add-Ons') ||
          input.description.includes('Perks')) {
        input.format = 'menu';
        console.log('Found menu format in object input');
      } else if (input.description.match(/^•\s+/m) || 
                (input.description.includes('\n\n') && input.description.match(/^[A-Z].*?:/m))) {
        input.format = 'intro-bullets';
        console.log('Found intro-bullets format in object input');
      } else {
        input.format = 'paragraphs';
      }
    }
    return input;
  }

  try {
    // Log the raw input for debugging
    console.log('Raw input type:', typeof input);
    console.log('Raw input length:', input.length);
    if (input.length < 100) {
      console.log('Raw input content:', input);
    } else {
      console.log('Raw input preview:', input.substring(0, 100) + '...');
    }

    const start = input.indexOf('{');
    const end = input.lastIndexOf('}');
    if (start >= 0 && end > start) {
      const clean = input.substring(start, end + 1);
      const parsed = JSON.parse(clean);
      
      // Detect format by looking for specific patterns in the description
      if (parsed.description && (
        parsed.description.includes('⸻') || // Has divider lines
        parsed.description.includes('---') || // Has alternative divider lines
        parsed.description.match(/^\d+\.\s+/m) || // Has numbered items
        parsed.description.includes('Add-Ons') || // Has add-ons section
        parsed.description.includes('Perks') || // Has perks section
        (parsed.format === 'menu') // Explicitly marked as menu format
      )) {
        parsed.format = 'menu';
        console.log('Detected menu format in description');
      } else if (parsed.description && (
        parsed.description.match(/^•\s+/m) || // Has bullet points
        (parsed.description.includes('\n\n') && parsed.description.match(/^[A-Z].*?:/m)) // Has intro paragraph and bullet-like structure
      )) {
        parsed.format = 'intro-bullets';
        console.log('Detected intro-bullets format in description');
      } else {
        parsed.format = 'paragraphs';
      }
      
      return parsed;
    }
  } catch (err) {
    console.error('Failed to parse input as JSON:', err);
  }

  // If we couldn't parse JSON, check the raw string for format patterns
  if (typeof input === 'string') {
    if (input.includes('⸻') || 
        input.includes('---') ||
        input.match(/^\d+\.\s+/m) ||
        input.includes('Add-Ons') ||
        input.includes('Perks')) {
      console.log('Detected menu format in raw string');
      return {
        logoUrl: '',
        imageUrl: '',
        name: 'Menu Special',
        description: input,
        format: 'menu'
      };
    } else if (input.match(/^•\s+/m) || 
              (input.includes('\n\n') && input.match(/^[A-Z].*?:/m))) {
      console.log('Detected intro-bullets format in raw string');
      return {
        logoUrl: '',
        imageUrl: '',
        name: 'Special Offer',
        description: input,
        format: 'intro-bullets'
      };
    }
  }

  return {
    logoUrl: '',
    imageUrl: '',
    name: "Chef's Special",
    description: input.length > 100 ? input.slice(0, 100) + '...' : input,
  };
};

const SpecialCardModule: React.FC<SpecialCardProps> = ({ input }) => {
  const data = parseSpecialData(input);
  const [imageError, setImageError] = useState(false);
  
  // Check if this is a menu format special that needs the structured card
  const isMenuFormat = data.format === 'menu' || (
    data.description && (
      data.description.includes('⸻') || // Has divider lines
      data.description.includes('---') || // Has alternative divider lines
      data.description.match(/^\d+\.\s+/m) || // Has numbered items
      data.description.includes('Add-Ons') || // Has add-ons section
      data.description.includes('Perks') // Has perks section
    )
  );
  
  // Log detection result for debugging
  console.log('Format detected:', data.format || 'not specified');
  console.log('Is menu format that needs structured card:', isMenuFormat);
  
  // Use the appropriate specialized component based on format
  if (isMenuFormat) {
    return <StructuredSpecialCard special={data} />;
  } else if (data.format === 'intro-bullets') {
    return <IntroBulletsSpecialCard special={data} />;
  }
  
  // Otherwise use the standard card format
  return (
    <div className="max-w-md mx-auto my-6 px-4">
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {data.logoUrl && (
          <div className="flex justify-center p-4 bg-gray-50">
            <img
              src={data.logoUrl}
              alt="Restaurant Logo"
              className="h-10 object-contain"
              loading="lazy"
            />
          </div>
        )}

        {data.imageUrl && (
          <img
            src={imageError ? 'https://via.placeholder.com/800x600/cccccc/888888?text=Image+Not+Available' : data.imageUrl}
            alt={data.name}
            className="w-full h-48 sm:h-64 object-cover"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        )}

        <div className="p-6">
          <h2 className="text-xl font-semibold text-center mb-2">{data.name}</h2>
          <p className="text-gray-700 text-sm text-center">{data.description}</p>
        </div>
      </div>
    </div>
  );
};

export default SpecialCardModule;
